import { TFunction } from 'i18next';
import {
  CheckCircleOutlined,
  CheckOutlined,
  ClockCircleOutlined,
  CloseCircleOutlined,
  DeleteOutlined,
  EditOutlined,
  MinusCircleOutlined,
  RollbackOutlined,
  StopOutlined,
  SyncOutlined,
} from '@ant-design/icons';

import { TableColumns, TableTagMappings } from '@ohpen/enso-ui';
import { ActionStatus, ChangeRequestResponse } from '@pmp/api';

const statusTooltip = (t: TFunction, status: ActionStatus) => ({ title: t(`global.labels.changeRequestStatusTooltips.${status}`) });

export const getActionStatusTagMappings = (t: TFunction): TableTagMappings => ({
  status: {
    [ActionStatus.DRAFT]: { color: 'default', icon: <EditOutlined />, tooltip: statusTooltip(t, ActionStatus.DRAFT) },
    [ActionStatus.PENDING_REVIEW]: {
      color: 'gold',
      icon: <ClockCircleOutlined />,
      tooltip: statusTooltip(t, ActionStatus.PENDING_REVIEW),
    },
    [ActionStatus.PENDING_RESUBMISSION]: {
      color: 'orange',
      icon: <RollbackOutlined />,
      tooltip: statusTooltip(t, ActionStatus.PENDING_RESUBMISSION),
    },
    [ActionStatus.APPROVED]: { color: 'geekblue', icon: <CheckOutlined />, tooltip: statusTooltip(t, ActionStatus.APPROVED) },
    [ActionStatus.PENDING_PROCESSING]: {
      color: 'blue',
      icon: <ClockCircleOutlined />,
      tooltip: statusTooltip(t, ActionStatus.PENDING_PROCESSING),
    },
    [ActionStatus.PROCESSING]: { color: 'blue', icon: <SyncOutlined spin />, tooltip: statusTooltip(t, ActionStatus.PROCESSING) },
    [ActionStatus.APPLIED]: { color: 'green', icon: <CheckCircleOutlined />, tooltip: statusTooltip(t, ActionStatus.APPLIED) },
    [ActionStatus.PARTIALLY_APPLIED]: {
      color: 'lime',
      icon: <MinusCircleOutlined />,
      tooltip: { title: t('global.labels.changeRequestErrorTooltip') },
    },
    [ActionStatus.NOT_APPLIED]: {
      color: 'volcano',
      icon: <CloseCircleOutlined />,
      tooltip: (record: unknown) => {
        const { processingError } = record as ChangeRequestResponse;
        return { title: processingError || t('global.labels.changeRequestErrorTooltip') };
      },
    },
    [ActionStatus.REJECTED]: { color: 'red', icon: <StopOutlined />, tooltip: statusTooltip(t, ActionStatus.REJECTED) },
    [ActionStatus.INVALIDATED]: {
      color: 'default',
      icon: <CloseCircleOutlined />,
      tooltip: (record: unknown) => {
        const { invalidationSource } = record as ChangeRequestResponse;
        const source = invalidationSource?.value || invalidationSource?.key;
        return {
          title: source
            ? t('global.labels.changeRequestInvalidatedTooltip', { source })
            : t('global.labels.changeRequestInvalidatedTooltipGeneric'),
        };
      },
    },
    [ActionStatus.DELETED]: { color: 'default', icon: <DeleteOutlined />, tooltip: statusTooltip(t, ActionStatus.DELETED) },
  },
});

const ACTION_STATUS_STAGES: { stage: string; statuses: ActionStatus[] }[] = [
  { stage: 'draft', statuses: [ActionStatus.DRAFT] },
  { stage: 'awaitingAction', statuses: [ActionStatus.PENDING_REVIEW, ActionStatus.PENDING_RESUBMISSION] },
  { stage: 'inProgress', statuses: [ActionStatus.APPROVED, ActionStatus.PENDING_PROCESSING, ActionStatus.PROCESSING] },
  { stage: 'completed', statuses: [ActionStatus.APPLIED, ActionStatus.PARTIALLY_APPLIED] },
  { stage: 'unsuccessful', statuses: [ActionStatus.NOT_APPLIED, ActionStatus.REJECTED] },
  { stage: 'closed', statuses: [ActionStatus.INVALIDATED, ActionStatus.DELETED] },
];

/** Groups the status column's filter by stage, so the twelve statuses read as six steps of one flow. */
export const withActionStatusFilterGroups = <T,>(columns: TableColumns<T>, t: TFunction): TableColumns<T> =>
  columns.map(column =>
    column.key === 'status'
      ? {
          ...column,
          options: {
            ...column.options,
            filterGroups: ACTION_STATUS_STAGES.map(({ stage, statuses }) => ({
              label: t(`global.labels.changeRequestStages.${stage}`),
              keys: statuses,
            })),
          },
        }
      : column,
  );
